/**
 * 精灵渲染器
 * 使用 CSS steps() 逐帧动画播放精灵图
 */
import { useMemo } from 'react'
import type { PetAnimationState } from './types'
import {
  DEFAULT_SPRITE_CONFIG,
  ANIMATION_ROW_MAP,
  ANIMATION_SPEED,
  ANIMATION_LOOPS,
  DISPLAY_SIZE,
} from './types'

interface SpriteRendererProps {
  spriteSrc: string
  animationState: PetAnimationState
  onAnimationEnd?: () => void
}

const SpriteRenderer = ({ spriteSrc, animationState, onAnimationEnd }: SpriteRendererProps) => {
  const { cols, rows } = DEFAULT_SPRITE_CONFIG
  const row = ANIMATION_ROW_MAP[animationState]
  const speed = ANIMATION_SPEED[animationState]
  const loops = ANIMATION_LOOPS[animationState]

  // 计算动画总时长
  const duration = speed * cols

  // 计算 background-position-y（按显示尺寸缩放后的值）
  const scaledSheetWidth = DISPLAY_SIZE * cols    // 96 * 8 = 768
  const scaledFrameHeight = DISPLAY_SIZE          // 96
  const bgPosY = -(row * scaledFrameHeight)

  // 精灵图总尺寸（缩放后）
  const bgWidth = scaledSheetWidth                // 768
  const bgHeight = scaledFrameHeight * rows       // 96 * 7 = 672

  const style = useMemo(
    () => ({
      width: DISPLAY_SIZE,
      height: DISPLAY_SIZE,
      backgroundImage: `url(${spriteSrc})`,
      backgroundSize: `${bgWidth}px ${bgHeight}px`,
      backgroundPositionY: `${bgPosY}px`,
      backgroundRepeat: 'no-repeat' as const,
      imageRendering: 'pixelated' as const,
      animation: `sprite-play ${duration}ms steps(${cols}) ${loops === 'infinite' ? 'infinite' : loops}`,
    }),
    [spriteSrc, bgWidth, bgHeight, bgPosY, duration, cols, loops]
  )

  // 使用 key 强制重新挂载以重启动画
  return (
    <div
      key={animationState}
      className="sprite-renderer"
      style={style}
      onAnimationEnd={loops !== 'infinite' ? onAnimationEnd : undefined}
    />
  )
}

export default SpriteRenderer
